import React, { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { F, CLSNAME, CLS } from "../data/aircraft";
import { STATS } from "../data/stats";
import { P } from "../data/places";
import { compute, parse, findPlace, TODAY_ISO } from "../lib/engine";
import { IC, BrandMark } from "../lib/icons";
import AircraftTile from "../components/AircraftTile";
import FleetCarousel from "../components/FleetCarousel";
import AircraftModal from "../components/AircraftModal";
import RequestModal from "../components/RequestModal";
import Footer from "../components/Footer";
import { useSettings } from "../context/SettingsContext";

const ORDER = ["heli", "turboprop", "light", "mid", "supermid", "large", "ulr", "bizliner", "group", "cargo", "piston"];
const EXAMPLES = ["Bombay to Bangalore on 12 December for 6 people", "Delhi to Udaipur wedding 13 guests", "Dehradun to Kedarnath by helicopter for 4", "Mumbai to Dubai 8 pax luxury", "Patna to Delhi air ambulance"];
const cityNames = Object.keys(P).sort();

export default function Home() {
  const s = useSettings();
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fDate, setFDate] = useState("");
  const [fPax, setFPax] = useState("");
  const [ask, setAsk] = useState("");
  const [S, setS] = useState({ from: "", to: "", pax: null, date: "", mode: "", med: false, lux: false, ret: false, searched: false, err: "", extraNotes: [] });
  const [cat, setCat] = useState("all");
  const [resLimit, setResLimit] = useState(12);
  const [fleetLimit, setFleetLimit] = useState(16);
  const [fleetQ, setFleetQ] = useState("");
  const [fleetSort, setFleetSort] = useState("size");
  const [modal, setModal] = useState(null); // {type:'ac'|'req', code, prefill}
  const resultsRef = useRef(null);

  const computed = useMemo(() => (S.searched && S.from && S.to ? compute(S) : { notes: [], res: [], dist: 0 }), [S]);
  const RES = computed.res;

  const doSearch = () => {
    const from = findPlace(fFrom), to = findPlace(fTo);
    const bad = [fFrom && !from ? fFrom : null, fTo && !to ? fTo : null].filter(Boolean);
    const extraNotes = [];
    if (!fPax && from && to) extraNotes.push({ c: "", t: "Add the number of guests to see only aircraft with enough seats." });
    setS({ from, to, pax: +fPax || null, date: fDate, mode: "", med: false, lux: false, ret: false, searched: true, extraNotes,
      err: bad.length ? `We couldn't find "${bad.join('", "')}". Try the nearest city with an airport, or pick from the suggestions.` : "Please enter where you're flying from and to." });
    setCat("all"); setResLimit(12);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const doAsk = () => {
    if (!ask.trim()) return;
    const p = parse(ask);
    const extraNotes = [];
    if (p.from && p.to && !p.pax && p.mode !== "cargo") extraNotes.push({ c: "", t: 'No guest count found, so we\'re showing all sizes. Add "for 6 people" to narrow it down.' });
    setFFrom(p.from || ""); setFTo(p.to || ""); setFPax(p.pax || ""); setFDate(p.date || "");
    setS({ ...p, searched: true, extraNotes, err: `We couldn't spot two places in "${ask}". Try something like "Mumbai to Goa on 20 December for 4 people".` });
    setCat("all"); setResLimit(12);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  // category counts
  const catCounts = useMemo(() => {
    const counts = {};
    const base = S.searched && RES.length ? RES.map((g) => g.f) : Object.keys(F).filter((k) => (STATS[k] || {}).n);
    base.forEach((fk) => (counts[F[fk].c] = (counts[F[fk].c] || 0) + 1));
    return { counts, total: base.length };
  }, [S.searched, RES]);

  // fleet list
  const fleetList = useMemo(() => {
    const q = fleetQ.trim().toLowerCase();
    let list = Object.keys(F).filter((fk) => (STATS[fk] || {}).n);
    if (cat !== "all") list = list.filter((fk) => F[fk].c === cat);
    if (q) list = list.filter((fk) => { const f = F[fk]; return (f.n + " " + f.mk + " " + CLSNAME[f.c] + " " + f.eng).toLowerCase().includes(q); });
    // Sliding order: high ticket-size jets first, down to low, helicopters last
    const rank = (fk) => (F[fk].k === "heli" ? 1 : 0);
    list.sort((a, b) => rank(a) - rank(b) || F[b].t - F[a].t || F[b].r - F[a].r);
    return list;
  }, [cat, fleetQ]);

  const totalFleet = useMemo(() => Object.keys(F).reduce((sum, k) => sum + ((STATS[k] || {}).n || 0), 0), []);
  const shownRes = RES.filter((g) => cat === "all" || F[g.f].c === cat);
  const pax = S.pax || 1;
  const dateTxt = S.date ? new Date(S.date + "T00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";

  const openAc = (code) => setModal({ type: "ac", code });
  const openReq = (code) => setModal({ type: "req", prefill: { from: S.from, to: S.to, date: S.date, pax: S.pax, ac: code || "", mode: S.mode, med: S.med } });

  return (
    <div className="sk">
      <datalist id="cityList">{cityNames.map((n) => <option key={n} value={n} />)}</datalist>

      <header className="top">
        <div className="hero" id="top">
          <div className="cloud c1" /><div className="cloud c2" /><div className="cloud c3" /><div className="cloud c4" />
          <div className="bigjet" aria-hidden="true"><img src="/hero_jet.webp" alt="" /></div>
          <div className="wrap">
            <div className="bar">
              <Link className="brand" to="/"><BrandMark /><span>{s.brand}</span></Link>
              <nav className="links" aria-label="Main">
                <Link to="/fleet">Our fleet</Link>
                <Link to="/how-it-works">How it works</Link>
                <button className="cta" type="button" onClick={() => openReq("")}>Request a flight</button>
              </nav>
            </div>
            <div className="hero-in">
              <h1>Private flights across India, <em>on your schedule</em></h1>
              <p className="sub">Jets, turboprops and helicopters for business trips, weddings, pilgrimages and campaigns. Tell us where and when, and we'll find the right aircraft.</p>
              <div className="search" role="search">
                <div className="seg"><label htmlFor="fFrom">From</label><input id="fFrom" list="cityList" placeholder="Departure city" autoComplete="off" value={fFrom} onChange={(e) => setFFrom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} /></div>
                <div className="seg"><label htmlFor="fTo">To</label><input id="fTo" list="cityList" placeholder="Where to?" autoComplete="off" value={fTo} onChange={(e) => setFTo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} /></div>
                <div className="seg"><label htmlFor="fDate">Date</label><input id="fDate" type="date" min={TODAY_ISO} value={fDate} onChange={(e) => setFDate(e.target.value)} /></div>
                <div className="seg"><label htmlFor="fPax">Guests</label><input id="fPax" type="number" min="1" max="60" placeholder="Add guests" value={fPax} onChange={(e) => setFPax(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} /></div>
                <button className="go" type="button" onClick={doSearch}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>Search</button>
              </div>
              <div className="ask">
                <div className="inp">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5l-1.9-4.6L5.5 9l4.6-1.4z" /></svg>
                  <input placeholder="Or just type it: Bombay to Bangalore on 12 December for 6 people" value={ask} onChange={(e) => setAsk(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAsk(); } }} />
                </div>
                <button className="btn2" type="button" onClick={doAsk}>Find my aircraft</button>
              </div>
              <div className="examples">
                {EXAMPLES.map((x) => <button key={x} type="button" onClick={() => { setAsk(x); setTimeout(doAsk, 0); }}>{x}</button>)}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="catbar"><div className="wrap"><div className="cats" role="toolbar" aria-label="Aircraft categories">
        {Object.entries(CLS).map(([k, v]) => {
          const n = k === "all" ? catCounts.total : (catCounts.counts[k] || 0);
          if (k !== "all" && !n) return null;
          return <button key={k} className="cat" type="button" aria-pressed={cat === k} onClick={() => { setCat(k); setResLimit(12); setFleetLimit(16); }}>{IC[v.ic]}<span>{v.n}</span></button>;
        })}
      </div></div></div>

      <main>
        <section className="block wrap" ref={resultsRef} aria-live="polite" hidden={!S.searched}>
          {S.searched && (!S.from || !S.to) ? (
            <div className="notes"><div className="note bad">{S.err}</div></div>
          ) : S.searched ? (
            <>
              <div className="shead">
                <div>
                  <div className="route"><b>{S.from} <span className="arrow">{"\u2192"}</span> {S.to}</b></div>
                  <div className="chips" style={{ marginTop: 8 }}>
                    {computed.dist ? <span className="chip">{Math.round(computed.dist).toLocaleString("en-IN")} nm {"\u00b7"} {Math.round(computed.dist * 1.852).toLocaleString("en-IN")} km</span> : null}
                    <span className="chip">{S.mode === "cargo" ? "Cargo" : pax + " guest" + (pax > 1 ? "s" : "")}</span>
                    {dateTxt ? <span className="chip">{dateTxt}</span> : null}
                    {S.ret ? <span className="chip">Return</span> : null}
                  </div>
                </div>
                <button className="btn primary" type="button" onClick={() => openReq("")}>Request a quote</button>
              </div>
              <div className="notes">{(S.extraNotes || []).concat(computed.notes).map((n, i) => <div key={i} className={"note " + (n.c || "")}>{n.t}</div>)}</div>
              {shownRes.length ? (
                <p style={{ margin: "0 0 16px", color: "var(--muted)" }}>{shownRes.length} aircraft type{shownRes.length > 1 ? "s" : ""} can fly this trip, sorted by {S.lux ? "cabin size" : "estimated cost"}. Tap one for full specs.</p>
              ) : RES.length ? (
                <div className="empty"><b>Nothing in this category for this trip</b>Pick "All" or another category.</div>
              ) : null}
              <div className="grid">{shownRes.slice(0, resLimit).map((g) => <AircraftTile key={g.f} code={g.f} g={g} onClick={() => openAc(g.f)} />)}</div>
              {shownRes.length > resLimit ? <div className="more"><button className="btn" type="button" onClick={() => setResLimit(999)}>Show all {shownRes.length}</button></div> : null}
            </>
          ) : null}
        </section>

        <section className="block wrap" id="fleet">
          <div className="shead">
            <div><h2>Our fleet</h2><p>{fleetList.length} aircraft type{fleetList.length === 1 ? "" : "s"}{cat !== "all" ? " in " + CLS[cat].n.toLowerCase() : ""} {"\u00b7"} from large jets down to helicopters, drawn from {totalFleet} aircraft flying with licensed Indian operators</p></div>
            <div className="fleettools">
              <input placeholder="Search aircraft, e.g. Legacy, King Air" value={fleetQ} onChange={(e) => setFleetQ(e.target.value)} aria-label="Search aircraft" />
            </div>
          </div>
          {fleetList.length ? <FleetCarousel items={fleetList} onSelect={openAc} /> : <div className="empty"><b>No aircraft match</b>Try another name or category.</div>}
        </section>

        <section className="block wrap" id="how">
          <div className="shead"><div><h2>How it works</h2><p>From enquiry to take-off, we handle the details.</p></div></div>
          <ol className="steps">
            <li><h3>Tell us your trip</h3><p>Route, date and number of guests. Search above or send a request.</p></li>
            <li><h3>Get options and a quote</h3><p>We check available aircraft with licensed operators and send you an itemised quote.</p></li>
            <li><h3>Confirm and pay</h3><p>We lock the aircraft, collect passenger details and arrange permits and ground transport.</p></li>
            <li><h3>Fly</h3><p>Arrive shortly before departure at the private terminal and board. We stay on call until you land.</p></li>
          </ol>
        </section>
      </main>

      <Footer />

      {modal?.type === "ac" ? <AircraftModal code={modal.code} g={RES.find((x) => x.f === modal.code)} dist={computed.dist} from={S.from} to={S.to} onClose={() => setModal(null)} onRequest={(code) => setModal({ type: "req", prefill: { from: S.from, to: S.to, date: S.date, pax: S.pax, ac: code, mode: S.mode, med: S.med } })} /> : null}
      {modal?.type === "req" ? <RequestModal prefill={modal.prefill || {}} onClose={() => setModal(null)} /> : null}
    </div>
  );
}
